/**
 * Telemetry Events - Strongly Typed Event Definitions
 * 
 * Central registry of all telemetry events used in AX1 | Capital.
 * This ensures consistent event naming across the codebase and enables
 * type-safe event tracking.
 * 
 * Naming Convention: domain.action (e.g., 'company_visibility.toggled')
 * 
 * @see telemetryClient.ts - Provider abstraction
 * @see visibilityTelemetry.ts - Domain-specific wrapper
 */

// ─────────────────────────────────────────────────────────────────────────────
// EVENT NAME CONSTANTS

type SimulatedWorkspaceRole = 'admin' | 'business_admin' | 'system_admin' | 'member';
// ─────────────────────────────────────────────────────────────────────────────

/**
 * All telemetry event names as constants
 * Use these instead of string literals for type safety
 */
export const TelemetryEvents = {
  // ─────────────────────────────────────────────────────────────────────────
  // Company Visibility Events
  // ─────────────────────────────────────────────────────────────────────────
  
  /** User viewed the companies linked to a project */
  CompanyVisibilityListLoaded: 'company_visibility.list_loaded',
  
  /** Admin/PM toggled visibility setting for a company */
  CompanyVisibilityToggled: 'company_visibility.toggled',
  
  /** A new company was linked to a project */
  CompanyVisibilityLinkCreated: 'company_visibility.link_created',
  
  // ─────────────────────────────────────────────────────────────────────────
  // Project Events (Future)
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Project was created */
  ProjectCreated: 'project.created',
  
  /** Project was viewed */
  ProjectViewed: 'project.viewed',
  
  /** Project tab was changed */
  ProjectTabChanged: 'project.tab_changed',
  
  // ─────────────────────────────────────────────────────────────────────────
  // Document Vault Events (Future)
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Document was uploaded */
  DocumentUploaded: 'document.uploaded',
  
  /** Document was downloaded */
  DocumentDownloaded: 'document.downloaded',
  
  /** Document access was requested */
  DocumentAccessRequested: 'document.access_requested',
  
  // ─────────────────────────────────────────────────────────────────────────
  // Auth Events (Future)
  // ─────────────────────────────────────────────────────────────────────────
  
  /** User signed in */
  AuthSignIn: 'auth.sign_in',
  
  /** User signed out */
  AuthSignOut: 'auth.sign_out',
  
  /** User session expired */
  AuthSessionExpired: 'auth.session_expired',
  
  // ─────────────────────────────────────────────────────────────────────────
  // User Management Events
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Generic feature usage tracking */
  FeatureUsed: 'feature.used',
  
  /** User was invited to workspace */
  UserInvited: 'user.invited',
  
  /** User was suspended */
  UserSuspended: 'user.suspended',
  
  /** User was reactivated */
  UserReactivated: 'user.reactivated',
  
  /** User role was changed */
  UserRoleChanged: 'user.role_changed',
  
  // ─────────────────────────────────────────────────────────────────────────
  // Users Page Events (Part E - Audit Logging + Telemetry)
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Users page was viewed */
  UsersPageView: 'users.page_view',
  
  /** Single invitation was sent */
  UsersInviteSent: 'users.invite_sent',
  
  /** Bulk invitations were sent (CSV import) */
  UsersInviteBulkSent: 'users.invite_bulk_sent',
  
  /** User inspector drawer was opened */
  UsersDrawerOpened: 'users.drawer_opened',
  
  /** User profile was updated */
  UsersProfileUpdated: 'users.profile_updated',
  
  /** Company affiliation was added */
  UsersCompanyAffiliationAdded: 'users.company_affiliation_added',
  
  /** Company affiliation was removed */
  UsersCompanyAffiliationRemoved: 'users.company_affiliation_removed',
  
  /** Workspace role was updated */
  UsersWorkspaceRoleUpdated: 'users.workspace_role_updated',
  
  /** Employment type was updated */
  UsersEmploymentTypeUpdated: 'users.employment_type_updated',
  
  /** User status was disabled (suspended) */
  UsersStatusDisabled: 'users.status_disabled',
  
  /** User status was enabled (reactivated) */
  UsersStatusEnabled: 'users.status_enabled',
  
  // ─────────────────────────────────────────────────────────────────────────
  // Role Management Events
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Custom role was created */
  RolesCreated: 'roles.created',
  
  /** Role was updated */
  RolesUpdated: 'roles.updated',
  
  /** Role was deleted */
  RolesDeleted: 'roles.deleted',
  
  /** Role was duplicated */
  RolesDuplicated: 'roles.duplicated',
  
  /** Duplicate role flow initiated (user clicked Duplicate button) */
  RolesDuplicateInitiated: 'roles.duplicate_initiated',
  
  /** Create from template modal opened (via duplicate or template selection) */
  RolesCreateFromTemplateOpened: 'roles.create_from_template_opened',
  
  /** User clicked View button on role card */
  RolesViewClicked: 'roles.view_clicked',
  
  /** User clicked Duplicate button on role card */
  RolesDuplicateClicked: 'roles.duplicate_clicked',
  
  /** User selected a template in Create Role modal */
  RolesTemplateSelected: 'roles.template_selected',
  
  // ─────────────────────────────────────────────────────────────────────────
  // RBAC Preview Events (Preview as User Simulator)
  // ─────────────────────────────────────────────────────────────────────────
  
  /** RBAC preview panel was opened */
  RbacPreviewOpened: 'rbac.preview_opened',
  
  /** RBAC preview settings were changed (role/employment/overrides) */
  RbacPreviewChanged: 'rbac.preview_changed',
  
  /** RBAC preview simulation was ended */
  RbacPreviewClosed: 'rbac.preview_closed',
  
  // ─────────────────────────────────────────────────────────────────────────
  // RBAC Export Events (Permissions CSV/PDF Export)
  // ─────────────────────────────────────────────────────────────────────────
  
  /** User clicked export button */
  RbacExportClicked: 'rbac.export_clicked',
  
  /** Export completed successfully */
  RbacExportSuccess: 'rbac.export_success',
  
  /** Export failed with error */
  RbacExportError: 'rbac.export_error',
  
  // ─────────────────────────────────────────────────────────────────────────
  // Role Template Compare Events
  // ─────────────────────────────────────────────────────────────────────────
  
  /** Role compare mode was opened */
  RolesCompareOpened: 'roles.compare_opened',
  
  /** Role compare filter was changed */
  RolesCompareFilterChanged: 'roles.compare_filter_changed',
  
  /** Role was reset to base template */
  RolesResetToTemplate: 'roles.reset_to_template',
  
} as const;

// Extract type from the const object
export type TelemetryEventName = typeof TelemetryEvents[keyof typeof TelemetryEvents];

// ─────────────────────────────────────────────────────────────────────────────
// EVENT PAYLOAD TYPES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Base properties included with every event
 * These are auto-enriched by the telemetry client
 */
export interface BaseEventProperties {
  /** ISO timestamp when event occurred */
  timestamp: string;
  /** Environment: 'demo' | 'dev' | 'staging' | 'production' */
  environment: string;
  /** Session ID for grouping related events */
  sessionId?: string;
}

/**
 * Company visibility event properties
 */
export interface CompanyVisibilityEventProperties {
  projectId: string;
  userId: string;
  companyId?: string;
  /**
   * Company-to-project relationship type. Canonical value only.
   * 'client' is a legacy database value; the normalizer maps it to 'capital_recipient'
   * before telemetry is emitted. Telemetry must never be used for authorization.
   */
  relationshipType?: 'internal' | 'capital_recipient' | 'portfolio_company' | 'borrower' | 'sponsor' | 'project_company' | 'investee' | 'partner' | 'investor' | 'vendor';
  newValue?: boolean;
  companyCount?: number;
  isAdmin?: boolean;
  bulkOperation?: boolean;
  targetTypes?: string[];
  workspaceId?: string;
}

/**
 * Project event properties
 */
export interface ProjectEventProperties {
  projectId: string;
  projectType?: string;
  projectStatus?: string;
  tabName?: string;
  userId?: string;
  workspaceId?: string;
}

/**
 * Document event properties
 */
export interface DocumentEventProperties {
  documentId: string;
  projectId: string;
  userId?: string;
  workspaceId?: string;
}

/**
 * Auth event properties
 */
export interface AuthEventProperties {
  userId?: string;
  method?: 'email' | 'oauth' | 'magic_link';
  workspaceId?: string;
}

/**
 * Users management event properties
 * Note: Contains only UUIDs/enums/counts - NO PII
 */
export interface UsersEventProperties {
  /** Index signature for sanitizeEventData compatibility */
  [key: string]: string | number | boolean | string[] | undefined;
  /** Current user performing the action (UUID) */
  actorId?: string;
  /** Target user being affected (UUID) */
  targetUserId?: string;
  /** Workspace context (UUID) */
  workspaceId?: string;
  /** Role ID being assigned/changed (UUID) */
  roleId?: string;
  /** Company ID for affiliation changes (UUID) */
  companyId?: string;
  /** Employment type enum */
  employmentType?: 'employee' | 'contractor' | 'intern' | 'vendor';
  /** New status after change */
  newStatus?: 'active' | 'inactive' | 'suspended';
  /** Previous status before change */
  previousStatus?: 'active' | 'inactive' | 'suspended';
  /** Previous role ID for role changes */
  previousRoleId?: string;
  /** Count for bulk operations */
  inviteCount?: number;
  /** Count of successful operations */
  successCount?: number;
  /** Count of failed operations */
  failedCount?: number;
  /** Source of the invite (single form or CSV import) */
  source?: 'form' | 'csv';
  /** Tab being viewed */
  tabName?: 'users' | 'roles' | 'invitations';
}

/**
 * Role management event properties
 * Note: Contains only UUIDs/enums/counts - NO PII
 */
export interface RolesEventProperties {
  /** Index signature for sanitizeEventData compatibility */
  [key: string]: string | number | boolean | string[] | undefined;
  /** Current user performing the action (UUID) */
  actorId?: string;
  /** Role being affected (UUID) */
  roleId?: string;
  /** Source role for duplication (UUID) */
  sourceRoleId?: string;
  /** Workspace context (UUID) */
  workspaceId?: string;
  /** Whether role is custom or system */
  isCustom?: boolean;
  /** Count of permissions in role */
  permissionCount?: number;
  /** Count of modules enabled */
  moduleCount?: number;
  /** Specific fields that were updated */
  updatedFields?: string[];
}

/**
 * RBAC Preview event properties
 * Used for "Preview as User" simulation feature
 * Note: Contains only UUIDs/enums - NO PII
 */
export interface RbacPreviewEventProperties {
  /** Index signature for sanitizeEventData compatibility */
  [key: string]: string | number | boolean | undefined;
  /** User being simulated (UUID) */
  userId?: string;
  /** Simulated workspace role */
  simulatedRole?: SimulatedWorkspaceRole;
  /** Simulated employment type */
  simulatedEmployment?: 'internal' | 'contractor' | 'external';
  /** Field that was changed during preview */
  field?: string;
  /** New value after change */
  newValue?: string | boolean;
  /** Duration of preview session (optional) */
  duration?: string;
  /** Whether overrides were included */
  includeOverrides?: boolean;
}

/**
 * RBAC Export event properties
 * Used for permissions CSV/PDF export feature
 * Note: Contains only UUIDs/enums/counts - NO PII
 */
export interface RbacExportEventProperties {
  /** Index signature for sanitizeEventData compatibility */
  [key: string]: string | number | boolean | undefined;
  /** Export format type */
  format?: 'csv' | 'pdf';
  /** Subject type being exported */
  subjectType?: 'user' | 'role';
  /** Subject ID (UUID) */
  subjectId?: string;
  /** Number of permissions in export */
  permissionCount?: number;
  /** Whether export includes draft changes */
  hasDraftChanges?: boolean;
  /** Error message if export failed */
  error?: string;
}

/**
 * Role template compare event properties
 * Used for role comparison feature
 * Note: Contains only UUIDs/enums/counts - NO PII
 */
export interface RolesCompareEventProperties {
  /** Index signature for sanitizeEventData compatibility */
  [key: string]: string | number | boolean | undefined;
  /** Role being edited (UUID) */
  roleId?: string;
  /** Base template being compared to */
  baseTemplateId?: string;
  /** Number of added permissions */
  addedCount?: number;
  /** Number of removed permissions */
  removedCount?: number;
  /** Number of level changes */
  levelChangedCount?: number;
  /** Filter applied to compare view */
  filter?: 'all' | 'changes_only';
  /** Module being reset (if module reset) */
  module?: string;
  /** Scope of reset */
  resetScope?: 'all' | 'module';
}

// ─────────────────────────────────────────────────────────────────────────────
// TYPE-SAFE EVENT MAP
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps event names to their expected payload types
 * Used by telemetryClient.track() for type inference
 */
export interface TelemetryEventPayloads {
  [TelemetryEvents.CompanyVisibilityListLoaded]: CompanyVisibilityEventProperties;
  [TelemetryEvents.CompanyVisibilityToggled]: CompanyVisibilityEventProperties;
  [TelemetryEvents.CompanyVisibilityLinkCreated]: CompanyVisibilityEventProperties;
  [TelemetryEvents.ProjectCreated]: ProjectEventProperties;
  [TelemetryEvents.ProjectViewed]: ProjectEventProperties;
  [TelemetryEvents.ProjectTabChanged]: ProjectEventProperties;
  [TelemetryEvents.DocumentUploaded]: DocumentEventProperties;
  [TelemetryEvents.DocumentDownloaded]: DocumentEventProperties;
  [TelemetryEvents.DocumentAccessRequested]: DocumentEventProperties;
  [TelemetryEvents.AuthSignIn]: AuthEventProperties;
  [TelemetryEvents.AuthSignOut]: AuthEventProperties;
  [TelemetryEvents.AuthSessionExpired]: AuthEventProperties;
  // Users management events
  [TelemetryEvents.UsersPageView]: UsersEventProperties;
  [TelemetryEvents.UsersInviteSent]: UsersEventProperties;
  [TelemetryEvents.UsersInviteBulkSent]: UsersEventProperties;
  [TelemetryEvents.UsersDrawerOpened]: UsersEventProperties;
  [TelemetryEvents.UsersProfileUpdated]: UsersEventProperties;
  [TelemetryEvents.UsersCompanyAffiliationAdded]: UsersEventProperties;
  [TelemetryEvents.UsersCompanyAffiliationRemoved]: UsersEventProperties;
  [TelemetryEvents.UsersWorkspaceRoleUpdated]: UsersEventProperties;
  [TelemetryEvents.UsersEmploymentTypeUpdated]: UsersEventProperties;
  [TelemetryEvents.UsersStatusDisabled]: UsersEventProperties;
  [TelemetryEvents.UsersStatusEnabled]: UsersEventProperties;
  // Role management events
  [TelemetryEvents.RolesCreated]: RolesEventProperties;
  [TelemetryEvents.RolesUpdated]: RolesEventProperties;
  [TelemetryEvents.RolesDeleted]: RolesEventProperties;
  [TelemetryEvents.RolesDuplicated]: RolesEventProperties;
  [TelemetryEvents.RolesDuplicateInitiated]: RolesEventProperties;
  [TelemetryEvents.RolesCreateFromTemplateOpened]: RolesEventProperties;
  [TelemetryEvents.RolesViewClicked]: RolesEventProperties;
  [TelemetryEvents.RolesDuplicateClicked]: RolesEventProperties;
  [TelemetryEvents.RolesTemplateSelected]: RolesEventProperties;
  // RBAC Preview events
  [TelemetryEvents.RbacPreviewOpened]: RbacPreviewEventProperties;
  [TelemetryEvents.RbacPreviewChanged]: RbacPreviewEventProperties;
  [TelemetryEvents.RbacPreviewClosed]: RbacPreviewEventProperties;
  // RBAC Export events
  [TelemetryEvents.RbacExportClicked]: RbacExportEventProperties;
  [TelemetryEvents.RbacExportSuccess]: RbacExportEventProperties;
  [TelemetryEvents.RbacExportError]: RbacExportEventProperties;
  // Role template compare events
  [TelemetryEvents.RolesCompareOpened]: RolesCompareEventProperties;
  [TelemetryEvents.RolesCompareFilterChanged]: RolesCompareEventProperties;
  [TelemetryEvents.RolesResetToTemplate]: RolesCompareEventProperties;
}

// ─────────────────────────────────────────────────────────────────────────────
// PRIVACY HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sanitize user data to ensure no PII is sent to analytics
 * Only allows UUIDs, booleans, enums, and counts
 */
export function sanitizeEventData<T extends Record<string, unknown>>(data: T): T {
  const sanitized = { ...data };
  
  // Remove any fields that might contain PII
  const piiFields = ['email', 'name', 'firstName', 'lastName', 'phone', 'address'];
  
  for (const field of piiFields) {
    if (field in sanitized) {
      delete sanitized[field];
    }
  }
  
  return sanitized;
}

/**
 * Validate that a string looks like a UUID (not an email or name)
 */
export function isValidUUID(value: string): boolean {
  // Basic UUID format check
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  // Also accept shorter IDs like 'proj-001', 'demo-company-001'
  const shortIdRegex = /^[a-z0-9-]+$/i;
  
  return uuidRegex.test(value) || (shortIdRegex.test(value) && !value.includes('@'));
}
